import type { AddressCheckVerificationResponse } from '@palmetto/solarflare-sdk';
import type { ObjectId } from 'mongodb';

import type { AccountMilestone, AccountMilestoneStatus } from './AccountMilestones';
import type { AccountRequirementDocument } from './AccountRequirements';
import type { AccountStatusTimelineEntry } from './AccountStatusChange';
import type { AgingAccountData, AgingQuery, AgingViewDocument } from './Aging';
import type { ApplicationDocument } from './Application';
import type { IncentiveType, RECIncentiveType } from './AssetIncentiveBlocks';
import type { FundEvent, TrancheStatus } from './AssetTypes';
import type { AttestmentData, InstallAttestmentItem, InstallAttestmentType } from './Attestment';
import type { DirectPayMaterialVendorAliasOptions } from './DirectPayTypes';
import type { DisclosureDefinition } from './Disclosures';
import type { DocumentDocument } from './DocumentTypes';
import type { AccountStatus, Meta } from './Entities';
import type { EquipmentException } from './EquipmentException';
import type { GlideECCoalTractEligibility, GlideECEligibility } from './GlideTypes';
import type { IsoDateString } from './Helpers';
import type { MilestoneTemplateType, MilestoneType } from './Milestones';
import type { ClawbackSummary } from './Payouts/Clawbacks';
import type { ProgramType } from './ProgramTypes';
import type { QueueDocument } from './Queues';
import type { QuoteDocument } from './Quotes';
import type { SalesRepStatus, SalesRepVerificationOverrideReason } from './SalesRepTypes';
import type {
  EnergyBackupSystemDesign,
  GeothermalSystemDesign,
  HVACSystemDesign,
  SystemDesign,
  SystemDesignReturnType,
  WaterHeaterSystemDesign,
  GeneratorSystemDesign,
} from './SystemDesigns';
import type { WarrantyProvider, WarrantyType } from './Warranties';
import type { MaintenanceContractStartEvent } from './Webhooks';

import type { Locale } from './index';

export interface RequirementsWithRequirements extends AccountRequirementDocument {
  // Recursive: a requirement's children are themselves nested with their own descendants.
  requirements: Array<RequirementsWithRequirements>;
}

export interface CurrentMilestone {
  accountMilestoneId: ObjectId;
  name: string;
  order: number;
  type: MilestoneTemplateType;
  status: AccountMilestoneStatus;
  requirements: Array<RequirementsWithRequirements>;
}

export interface AccountMilestoneWithRequirements extends Omit<
  AccountMilestone,
  'requirementsRefs' | 'statusTimeline'
> {
  requirements: Array<RequirementsWithRequirements>;
}

export type ProjectDates = Record<string, string>;

export enum CancelActivityRequesterType {
  customer = 'customer',
  organization = 'organization',
  internal = 'internal',
}

export enum CancelReason {
  financialConcerns = 'financialConcerns',
  ineligiblePropertyType = 'ineligiblePropertyType',
  choseDifferentFinancialProvider = 'choseDifferentFinancialProvider',
  unhappyWithSalesProcess = 'unhappyWithSalesProcess',
  reasonNotProvided = 'reasonNotProvided',
}

export const cancelReasonLabels: Record<CancelReason, string> = {
  [CancelReason.financialConcerns]: 'Financial Concerns',
  [CancelReason.ineligiblePropertyType]: 'Ineligible Property Type',
  [CancelReason.choseDifferentFinancialProvider]: 'Chose Different Financial Provider',
  [CancelReason.unhappyWithSalesProcess]: 'Unhappy With Sales Process',
  [CancelReason.reasonNotProvided]: 'Reason Not Provided',
};

export const cancelActivityRequesterTypeLabels: Record<CancelActivityRequesterType, string> = {
  [CancelActivityRequesterType.organization]: 'Organization',
  [CancelActivityRequesterType.customer]: 'Customer',
  [CancelActivityRequesterType.internal]: 'LightReach',
};

export enum CancelActivityType {
  cancellation = 'cancellation',
  reactivation = 'reactivation',
}

export enum CEDParallelPayStatus {
  pending = 'pending',
  paid = 'paid',
  scheduled = 'scheduled',
}

export enum DirectPayParallelPayStatus {
  pending = 'pending',
  paid = 'paid',
  scheduled = 'scheduled',
}

export enum CedProgramTypes {
  parallelPay = 'Lightreach v2024 2.0',
  postPay = 'Lightreach v2024 1.0',
}

export const CedProgramNameLabels: Record<CedProgramTypes, string> = {
  [CedProgramTypes.parallelPay]: 'Parallel Pay',
  [CedProgramTypes.postPay]: 'Post Pay',
};

export enum DomesticContentEligibilityLabels {
  eligible = 'Eligible - Unconfirmed',
  underConsideration = 'Under Consideration',
  epcOptOut = 'Ineligible - EPC Opt Out',
  confirmed = 'Confirmed',
}

export enum DomesticContentEligibilityTypes {
  eligible = 'eligible',
  underConsideration = 'underConsideration',
  epcOptOut = 'epcOptOut',
  confirmed = 'confirmed',
}

export const DC_ELIGIBLE_STATUSES: DomesticContentEligibilityTypes[] = [
  DomesticContentEligibilityTypes.eligible,
  DomesticContentEligibilityTypes.underConsideration,
  DomesticContentEligibilityTypes.confirmed,
];

export enum SafeHarborEquipmentStatus {
  underConsideration = 'underConsideration',
  confirmed = 'confirmed',
  rejected = 'rejected',
}

export enum SafeHarborEquipmentStatusLabels {
  underConsideration = 'Under Consideration',
  confirmed = 'Confirmed',
  rejected = 'Rejected',
}

export enum InboundPaymentProvider {
  concord = 'concord',
  /** @deprecated */
  stripe = 'stripe',
}

export enum DirectPayProjectStatus {
  active = 'active',
  optOut = 'optOut',
  cancelled = 'cancelled',
  onHold = 'onHold',
  processing = 'processing', // transient: async opt-in/opt-out in flight
}

export enum DirectPayProjectStatusLabel {
  active = 'Active',
  optOut = 'Opt Out',
  cancelled = 'Cancelled',
  onHold = 'On Hold',
  processing = 'Processing',
}

export enum PropertyType {
  townhome = 'townhome',
  manufacturedHome = 'manufacturedHome',
  singleFamily = 'singleFamily',
  condo = 'condo',
  multiFamily = 'multiFamily',
  other = 'other',
}

export const propertyTypeLabelsMap: Record<PropertyType, string> = {
  [PropertyType.townhome]: 'Townhome',
  [PropertyType.manufacturedHome]: 'Manufactured Home',
  [PropertyType.singleFamily]: 'Single Family',
  [PropertyType.condo]: 'Condo',
  [PropertyType.multiFamily]: 'Multi Family',
  [PropertyType.other]: 'Other',
};

export interface PaymentMethodHistory {
  paymentMethod: 'string';
  dateSet: Date;
}

export enum BillingEventType {
  accountCancelledInLightReach = 'Billing attempted but account was cancelled in lightReach',
  accountCancelledInConcord = 'Billing attempted but account was cancelled in concord',
  billingRequirementsNotMet = 'Billing attempted but billing requirements not met',
  accountHasNoActiveContract = 'Billing attempted but there was no active contract',
  firstPaymentDueDateRemoved = `First payment due date removed in order to resend billing in the future`,
  billingSent = 'Billing schedule was sent to Concord',
  billingBlock = 'Billing block',
  billingPrevented = 'Billing prevented',
  billingError = 'Billing failed due to error during Concord billing',
  preBillingAdjustmentOfferSent = 'Pre-billing adjustment offer sent to customer',
  measurableGenerationDateSet = 'Measurable generation date set, billing may need to be recalculated',
  measurableGenerationAttemptedWithoutOperationAt = 'Measurable generation date set attempted but operationAtDate has not been set',
  concordAccountReset = 'Concord account data reset',
  oneTimePaymentScheduled = 'One-time payment scheduled',
  oneTimePaymentCancelled = 'One-time payment cancelled',
  preBillingAdjustment = 'Pre-billing adjustment',
  directPaymentLinkCreated = 'Direct payment link created',
  directPaymentLinkDeactivated = 'Direct payment link deactivated',
  directPaymentLinkGenerated = 'Direct payment link generated',
}
export enum BillingAction {
  billingPrevented = 'billingPrevented',
  firstPaymentDueDateRemoved = 'firstPaymentDueDateRemoved',
  billingSent = 'billingSent',
  billingBlockAdded = 'billingBlockAdded',
  billingBlockRemoved = 'billingBlockRemoved',
  billingFailed = 'billingFailed',
  preBillingAdjustmentOffer = 'preBillingAdjustmentOffer',
  measurableGenerationDateSet = 'measurableGenerationDateSet',
  measurableGenerationDateSetAgain = 'measurableGenerationDateSetAgain',
  measurableGenerationAttemptedWithoutOperationAt = 'measurableGenerationAttemptedWithoutOperationAt',
  concordAccountReset = 'concordAccountReset',
  oneTimePaymentScheduled = 'oneTimePaymentScheduled',
  oneTimePaymentCancelled = 'oneTimePaymentCancelled',
  preBillingAdjustmentVoided = 'preBillingAdjustmentVoided',
  preBillingAdjustmentReinstated = 'preBillingAdjustmentReinstated',
  directPaymentLinkCreated = 'directPaymentLinkCreated',
  directPaymentLinkDeactivated = 'directPaymentLinkDeactivated',
  directPaymentLinkGenerated = 'directPaymentLinkGenerated',
}

export interface CustomerPaymentLink {
  sentAt: Date;
  // Empty array means the link was generated only (not sent).
  deliveryMethods: ('email' | 'sms')[];
  sentToEmail?: string;
  sentToPhone?: string;
  sentBy: string; // agent user id
  sentByEmail?: string;
  expiresAt: Date;
  applicantType: ApplicantType;
  link: string;
  inviteId?: ObjectId; // id of the invite that backs this link; used to scope per-link mutations
  description?: string;
  linkSuccessfullyUsedAt?: Date;
  firstOpenedAt?: Date;
  lastOpenedAt?: Date;
  minAllowedPayment?: number; // minimum payment the customer can submit; 0 means no minimum
  deactivatedAt?: Date; // set when a user manually deactivates the link; distinct from natural expiry
}

export interface BillingEvents {
  action: BillingAction;
  type: BillingEventType;
  notes?: string;
  date: Date;
  user?: string;
  userEmail?: string;
  shouldBlockTranche?: boolean;
  preBillingDiscountApplicantAcceptances?: PreBillingDiscountApplicantAcceptance[];
  amendmentDocumentIds?: string[];
  contractId?: string;
  amount?: number;
  fromPaymentLink?: boolean;
  paymentLinkSentTo?: string;
  enrollInAutoPay?: boolean;
}

export interface BillingBlock {
  reason: string; // Reason ID from BlockBillingSettings
  appliedDate: Date;
  appliedBy: string; // userId or 'system'
  appliedByEmail?: string; // User email for display
  notes: string;
  metadata?: Record<string, any>; // Additional context
  expiresAt?: Date; // Optional auto-expiration
  shouldBlockTranche?: boolean;
}

export interface BillingStatusHistory {
  billingStatus: 'string';
  billingStatusDescription: 'string';
  lastUpdatedTimestamp: Date;
  asOfDate: Date;
  sequenceNumber: number;
}

export enum CurrentPaymentMethod {
  statement = 'statement',
  autoAch = 'autoAch',
  autoCreditCard = 'autoCreditCard',
}

export const currentPaymentMethodLabels: Record<CurrentPaymentMethod, string> = {
  [CurrentPaymentMethod.statement]: 'Statement',
  [CurrentPaymentMethod.autoAch]: 'Auto ACH',
  [CurrentPaymentMethod.autoCreditCard]: 'Auto Credit Card',
};

export interface InboundPaymentProviderData {
  provider: InboundPaymentProvider;
  providerUrl?: string;
  startDate: Date;
  existsInConcordsSystem?: boolean;
  currentPaymentMethod?: CurrentPaymentMethod;
  paymentMethodHistory?: PaymentMethodHistory[];
  currentBillingStatus?: string;
  /**
   * Ordering key for delinquency writes: the ISO datetime the status above was
   * acquired from Concord — the change row's LastUpdateDate, or the poll time when
   * Concord left it null. A sync writes unless the stored value is newer; equal
   * keys re-apply idempotently.
   */
  delinquencyRefreshedAt?: string;
  billingStatusHistory?: BillingStatusHistory[];
  billingEvents?: BillingEvents[];
  customerPaymentLinks?: CustomerPaymentLink[];
}

export interface InboundPaymentProviderHistory extends InboundPaymentProviderData {
  migrationDate: Date;
}

export interface Cancellation {
  activityType: CancelActivityType;
  isCancelled: boolean;
  notes?: string;
  requestedBy: string;
  requestedByDisplayName?: string;
  requestedByOrgAlias?: string;
  requestedByOrgDisplayName?: string;
  requestedDate: Date;
  requestHistory?: Cancellation[];
  requesterType: CancelActivityRequesterType;
  reason: CancelReason;
  supportingDocuments?: DocumentDocument[];
}

export interface SalesRepVerification {
  salesRepReferenceId: ObjectId;
  status?: SalesRepStatus;
  overriddenBy?: { id: string; name: string };
  overrideReasons?: SalesRepVerificationOverrideReason[];
}

export interface BaseAccountDetails {
  externalReference?: string;
  externalReferenceIds?: Array<ExternalReferenceId>;
  programType: string;
  friendlyName: string;
  address: Address;
  applicants?: Array<Applicant>;
  coordinates?: Coordinates;
  countyResolution?: CountyResolution;
  isNewYorkCityAddress?: boolean;
  language?: Locale;
  utility: Utility;
  electricityUsageKwh?: number | number[];
  electricityUsageSource?: string | undefined;
  salesRepName: string;
  salesRepLicenseNumber?: string;
  salesRepEmail?: string;
  salesRepVerification?: SalesRepVerification;
  salesRepPhoneNumber?: string;
  notes?: AccountNotes;
  strandedFulfillment?: StrandedFulfillment;
}

export interface SolarAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: SystemDetails;
  systemDesign?: SystemDesignReturnType<ProgramType.solar>;
}

export interface WarrantyContract {
  provider: WarrantyProvider;
  contracts: Array<{
    contractId: string;
    dealerInvoiceNumber: string;
  }>;
  submissionDate: Date;
}

export interface HVACAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: HVACSystemDesign;
  systemDesign?: HVACSystemDesign;
  systemInstallDate?: ProjectDates;
  /**
   * @deprecated no longer used
   */
  incentiveAttestation?: AttestmentData;
  permitAttestation?: AttestmentData;
  ntpAttestment?: AttestmentData;
  propertyDetails?: HVACPropertyDetails;
  warrantySettings?: Record<WarrantyType, ObjectId | undefined>;
  warrantyContracts?: WarrantyContract;
}

export enum EnergyBackupExistingSystemFinanceType {
  cash = 'cash',
  leasePPA = 'leasePPA',
  loan = 'loan',
  unknown = 'unknown',
}

export interface EnergyBackupAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: EnergyBackupSystemDesign;
  systemDesign?: EnergyBackupSystemDesign;
  existingSystemDetails?: {
    hasExistingSystem: boolean;
    acSystemSize?: number;
    financeType?: EnergyBackupExistingSystemFinanceType;
    acSystemSizeAttestation?: AttestmentData;
  };
}

export interface WaterHeaterPropertyDetails {
  homeSize?: number;
  existingSystems?: number;
  systemsBeingReplaced?: number;
}

export interface WaterHeaterAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: WaterHeaterSystemDesign;
  systemDesign?: WaterHeaterSystemDesign;
  systemInstallDate?: ProjectDates;
  projectAttestation?: AttestmentData;
  ntpAttestment?: AttestmentData;
  propertyDetails?: WaterHeaterPropertyDetails;
  warrantySettings?: Record<WarrantyType, ObjectId | undefined>;
  warrantyContracts?: WarrantyContract;
}

export interface GeneratorPropertyDetails {
  numberOfSystems?: number;
}

export interface GeneratorAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: GeneratorSystemDesign;
  systemDesign?: GeneratorSystemDesign;
  systemInstallDate?: ProjectDates;
  projectAttestation?: AttestmentData;
  ntpAttestment?: AttestmentData;
  propertyDetails?: GeneratorPropertyDetails;
  warrantySettings?: Record<WarrantyType, ObjectId | undefined>;
  warrantyContracts?: WarrantyContract;
}

export interface GeothermalPropertyDetails {
  homeSize?: number;
}

export interface GeothermalAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: GeothermalSystemDesign;
  systemDesign?: GeothermalSystemDesign;
  systemInstallDate?: ProjectDates;
  projectAttestation?: AttestmentData;
  propertyDetails?: GeothermalPropertyDetails;
  warrantySettings?: Record<WarrantyType, ObjectId | undefined>;
  warrantyContracts?: WarrantyContract;
}

export interface ConsumerSolarAccountDetails extends BaseAccountDetails {
  /** @deprecated */
  systemDetails?: SystemDetails;
  systemDesign?: SystemDesignReturnType<ProgramType.consumerSolar>;
}

export type AccountDetails<T extends ProgramType> = T extends 'hvac'
  ? HVACAccountDetails
  : T extends 'energyBackup'
    ? EnergyBackupAccountDetails
    : T extends 'waterHeater'
      ? WaterHeaterAccountDetails
      : T extends 'generator'
        ? GeneratorAccountDetails
        : T extends 'geothermal'
          ? GeothermalAccountDetails
          : T extends 'consumerSolar'
            ? ConsumerSolarAccountDetails
            : SolarAccountDetails;

export type TCEDObject = {
  programName?: string;
  cedProjectId?: string;
  cedSplitPayEnabled: boolean;
  projectCreatedDate?: Date;
  parallelPaymentAmount?: number;
  parallelPaymentStatus?: CEDParallelPayStatus;
  shipConfirmationDate?: Date;
  shipConfirmationReceivedDate?: Date;
  spaPricingEnabled?: boolean;
  invoiceFinalizationDate?: Date;
  projectStatus?: DirectPayProjectStatus;
  optOutDate?: Date;
  totalPaymentAmount?: number;
  totalPaymentStatus?: CEDParallelPayStatus;
};

export type DirectPaySettingsBase = {
  directPayMaterialVendorAlias: DirectPayMaterialVendorAliasOptions;
  subCompanyId?: string;
  projectIdentifier: string;
  directPayEnabled: boolean;
  projectCreatedDate?: Date;
  projectId?: string;
  parallelPaymentAmount?: number;
  parallelPaymentStatus?: CEDParallelPayStatus;
  shipConfirmationDate?: Date;
  shipConfirmationReceivedDate?: Date;
  invoiceFinalizationDate?: Date;
  projectStatus?: DirectPayProjectStatus;
  optOutDate?: Date;
  programName?: string;
  totalPaymentAmount?: number;
  totalPaymentStatus?: CEDParallelPayStatus;
};

export type DirectPayHistoryEntry = Omit<DirectPaySettingsBase, 'directPayEnabled' | 'projectStatus'> & {
  archivedAt: Date;
};

export type DirectPayOverrideGate = 'ntp' | 'install' | 'shipmentConfirmation' | 'finalizationInvoice';

export interface DirectPaySupportOverride {
  action: 'optIn' | 'optOut';
  reason: string;
  salesforceTicketNumber?: string;
  // Which gates this override bypassed, captured at request time.
  bypassedGates: DirectPayOverrideGate[];
  overriddenByUserId?: string;
  overriddenByEmail?: string;
  createdAt: Date;
}

// Request-side shape: presence of this object (vs. a separate boolean flag) signals an override.
export interface DirectPaySupportOverrideInput {
  reason: string;
  salesforceTicketNumber?: string;
}

export type DirectPaySettings = DirectPaySettingsBase & {
  directPayHistory?: DirectPayHistoryEntry[];
  supportOverrides?: DirectPaySupportOverride[];
};

export interface IElectricianSignOffDate {
  palmetto: Date;
}

export interface MonthlyPaymentCap {
  override?: boolean;
  overrideDate?: Date;
  overrideUserId?: string;
  overrideUserName?: string;
  zeroEscalationRate: number;
  greaterThanZeroEscalationRate: number;
  orgMaxZeroEscalationRate?: number;
  orgMaxGreaterThanZeroEscalationRate?: number;
  capTableVersionUsed: number;
  dateCapsApplied: Date;
}

type VerifyAddressResult = AddressCheckVerificationResponse['meta'] extends {
  result: {
    eam?: infer EamType;
    risk?: {
      score: infer ScoreType;
      tumbling_risk: infer TumblingRiskType;
      postal?: infer PostalType;
      phone?: infer PhoneType;
    };
  };
}
  ? {
      eam?: EamType;
      risk?: {
        score: ScoreType;
        tumbling_risk: TumblingRiskType;
        postal?: PostalType;
        phone?: PhoneType;
      };
    }
  : never;

export enum VerifyAddressProvider {
  atData = 'AtData',
}

export interface VerifyAddressResponse {
  provider: VerifyAddressProvider;
  verdict: AddressCheckVerificationResponse['verdict'];
  eam?: AddressCheckVerificationResponse['meta'] extends VerifyAddressResult['eam']
    ? VerifyAddressResult['eam']
    : never;
  risk?: AddressCheckVerificationResponse['meta'] extends VerifyAddressResult['risk']
    ? VerifyAddressResult['risk']
    : never;
}

export enum IncentiveAssignmentStatus {
  active = 'active',
  complete = 'complete',
  blocked = 'blocked',
  unallocated = 'unallocated',
}

export type AssetIncentiveAssignment = {
  allocationId: ObjectId;
  allocationName: string;
  incentiveKey: string;
  incentiveType: IncentiveType;
  recType: RECIncentiveType;
  status: IncentiveAssignmentStatus;
  dateAssigned: Date;
  dateRemoved?: Date;
  blockDate?: Date;
  blockReason?: string;
  endReason?: string;
  /** When this was added to the history */
  supersededAt?: Date;
};

export type AssetIncentiveDetails = {
  history: AssetIncentiveAssignment[];
  currentAssignments: AssetIncentiveAssignment[];
};

export type AccountDocument<T extends ProgramType = any> = AccountDetails<T> & {
  id: ObjectId;
  organizationId: string;
  licensedOrganizationId?: string;
  materialsVendorOrgAlias?: string;
  status?: AccountStatus;
  statusTimeline?: AccountStatusTimelineEntry[];
  /** @deprecated */
  stripePaymentMethodVerificationMethod?: VerificationMethod;
  /** @deprecated */
  stripeCustomerId?: string;
  /** @deprecated */
  stripePaymentMethodId?: string;
  /** @deprecated */
  stripeSetupIntentId?: string;
  priceSheetId?: string;
  primaryApplicantName?: string;
  /** @deprecated */
  systemDesigns?: Array<SystemDesign>;
  monitoringSiteId?: string;
  electricianSignoffDate?: IElectricianSignOffDate;
  expectedPTODate?: ProjectDates;
  ptoGrantedDate?: IsoDateString;
  epcIncorrectSubmittedPtoGrantedDate?: IsoDateString;
  ptoGreenlight?: PtoGreenlight;
  itcAdderQualifications?: ITCAdderQualifications;
  meta?: Meta;
  allMilestonesComplete?: boolean;
  currentMilestone: CurrentMilestone;
  milestones: Array<AccountMilestoneWithRequirements>;
  priceSheetMappingId?: ObjectId;
  priceSheetMappingExpiresAt?: Date;
  pricingSettingLockDate?: Date;
  permitSubmittedDate?: ProjectDates;
  permitApprovedDate?: ProjectDates;
  drillerName?: string;
  drillerLicense?: string;
  installScheduledDate?: ProjectDates;
  scheduledInspectionDate?: ProjectDates;
  cancellation?: Cancellation;
  ced?: TCEDObject;
  clawbacks?: Array<ClawbackSummary>;
  /** Set when the cancellation clawback sweep has processed this account. Used to drain the daily work set. */
  cancellationClawbackProcessedAt?: Date;
  inboundPayments?: InboundPaymentProviderData & {
    providerHistory: InboundPaymentProviderHistory[];
  };
  installer?: Installer;
  batteryMonitoringSiteId?: string;
  designTool?: string;
  noInverterConsumptionCT?: boolean;
  noBatteryConsumptionCT?: boolean;
  funds?: string[];
  tranches?: string[];
  trancheHistory?: Array<{
    event: FundEvent;
    trancheName: string;
    trancheStatus: TrancheStatus;
    date: Date;
    user: string;
  }>;
  incentiveAllocations?: AssetIncentiveDetails;
  estimatedStatementGenerationDate?: Date;
  firstPaymentDueDate?: string;
  firstPaymentDueDateTrigger?: string;
  orgEnergyCommunityModifierId?: ObjectId;
  orgHoldbackId?: ObjectId;
  orgPpwModifierId?: ObjectId;
  programType?: ProgramType;
  acquisitionChannel?: string;
  brand?: string;
  originatingPlatform?: string;
  orgDomesticContentModifierId?: ObjectId;
  workOrderTotalPrice?: number | null;
  isDomesticContentEligible?: boolean;
  payoutMigrationComplete?: boolean;
  shouldUseDomesticContentPricing?: boolean;
  solarModuleSerialNumber?: string;
  communityName?: string;
  domesticContentEligibilityStatus?: DomesticContentEligibilityTypes;
  safeHarborEquipmentStatus?: SafeHarborEquipmentStatus | null;
  monthlyPaymentCap?: MonthlyPaymentCap;
  directPay?: DirectPaySettings;
  maintenanceContract?: MaintenanceContractStartEvent;
  billingBlocks?: BillingBlock[];
  /** @deprecated */
  waitingForTrancheToStartBilling?: boolean;
  isNewYorkCityAddress?: boolean;
  trueUpSizeApprovalDate?: Date;
  operationAtData?: {
    operationAtDate?: Date | null;
    verificationDaysTrigger?: VerificationDaysTrigger;
    verificationDate?: Date | null;
    notes?: { note: string; date: Date | null }[];
  };
  placedInServiceData?: {
    placedInServiceDate?: Date | null;
    placedInServiceTrigger?: PlacedInServiceTrigger;
    notes?: { note: string; date: Date | null }[];
  };
  opAtHistory?: Array<{
    eventType: OperationAtEventType;
    date: Date;
    triggerEvent?: VerificationDaysTrigger | PlacedInServiceTrigger;
    verificationDate?: Date | null;
    operationAtDate?: Date;
    placedInServiceDate?: Date;
    requirementsNotMet?: VerificationDaysTrigger[];
    verificationDays?: number;
    maxVerificationDays?: number;
    note?: string;
  }>;
  sundialFirstMeasurableGenerationDate?: Date;
  sundialMonitoringVerifiedAt?: string;
  sundialSiteId?: string;
  propertyType?: PropertyType;
  isSecondSystem?: boolean;
  equipmentException?: EquipmentException | null;
  preBillingCustomerDiscount?: PreBillingCustomerDiscountStatus;
  preBillingAdjustmentOfferLinks?: Array<{ email: string; url: string }>;
  preBillingDiscountApplicantAcceptances?: Array<PreBillingDiscountApplicantAcceptance>;
  executedEscrowAgreementDocumentDate?: IsoDateString;
  dispatch?: {
    previousHoldingOrg?: string;
  };
  attestmentInformation?: Partial<Record<InstallAttestmentType, InstallAttestmentItem>>;
  bundleGroupId?: ObjectId;
};

export enum PreBillingCustomerDiscountStatus {
  open = 'open',
  voided = 'voided',
  expired = 'expired',
  accepted = 'accepted',
}

export interface PreBillingCustomerDiscountAcceptance {
  acceptedAt: Date;
  acceptedIpAddress: string;
  acceptedUserAgent: string;
}

export interface PreBillingDiscountApplicantAcceptance extends PreBillingCustomerDiscountAcceptance {
  email: string;
}

export enum VerificationDaysTrigger {
  installApproved = 'installApproved',
  ptoConfirmation = 'ptoConfirmation',
  electricianSignOff = 'electricianSignOff',
  trancheClosed = 'trancheClosed',
  trueUpSize = 'trueUpSize', // Currently not used as it is a child of installApproved
}

export enum PlacedInServiceTrigger {
  operationAt = 'operationAt',
  measurableGeneration = 'measurableGeneration',
  trancheClosed = 'trancheClosed',
}

export enum OperationAtEventType {
  operationAtCalculated = 'operationAtCalculated',
  verificationDaysCalculated = 'verificationDaysCalculated',
  verificationDaysExpiredAndReqNoLongerMet = 'verificationDaysExpiredAndReqNoLongerMet',
  placedInServiceCalculated = 'placedInServiceCalculated',
}

export type OperationAtRequirements = {
  trancheClosedRequired: boolean;
  trueUpSizeRequired: boolean;
  ptoApprovedRequired: boolean;
  electricianSignOffRequired: boolean;
  installApprovedRequired: boolean;
  calcOperationDoesNotNeedToRunForThisTriggerEvent: boolean;
};

export type Account<T extends ProgramType = any> = Omit<AccountDocument<T>, 'id'> & {
  id: string;
};

export type Installer = {
  legalName: string;
  iccDocketNumber: string;
  qualifiedPersonOnsiteFirstName: string;
  qualifiedPersonOnsiteLastName: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
};

export enum VerificationMethod {
  instant = 'instant',
  skip = 'skip',
}

export type ApplicantDisclosure = Omit<DisclosureDefinition, 'versionNumber'> & { accepted?: boolean };

export interface EmailVerificationResult {
  passed: boolean;
  checkedAt: Date;
}

export interface Applicant {
  id?: ObjectId; // integrators are passing strings for this value and using it as thier internal identifier
  type: ApplicantType;
  firstName: string;
  lastName: string;
  middleName?: string;
  phoneNumber: string;
  email: string;
  address: Address;
  disclosures?: Array<ApplicantDisclosure>; // default should be empty array
  creditDOB?: string;
  addressVerification?: VerifyAddressResponse;
  emailVerification?: EmailVerificationResult | null;
  applicantId?: ObjectId; // added for our internal use
  externalReferenceIds?: Array<ExternalReferenceId>;
}

export interface Address {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

export enum ApplicantType {
  primary = 'primary',
  secondary = 'secondary',
}

export type Coordinates = {
  lat: number;
  lon: number;
};

export enum CountyResolutionStatus {
  resolved = 'resolved',
  /** GLIDE answered, but no county covers the coordinates. Terminal, no retry. */
  notFound = 'notFound',
  /** More than one county matched, so GLIDE stopped behaving as a point-in-polygon query. */
  ambiguous = 'ambiguous',
  /** Geocoding produced the {lat:0, lon:0} sentinel, so GLIDE was never asked. */
  noCoordinates = 'noCoordinates',
  /**
   * Caller-supplied coordinates did not land near the geocoded address, so GLIDE was never
   * asked. Terminal: a retry would resolve the same wrong point.
   */
  unverifiedCoordinates = 'unverifiedCoordinates',
  /** GLIDE was unreachable, or the address changed and invalidated a previous result. Retried async. */
  pending = 'pending',
}

export interface CountyResolution {
  status: CountyResolutionStatus;
  /** County FIPS from GLIDE's `geoid`. String, not number: codes are zero-padded, e.g. '02020'. */
  geoid?: string;
  /** GLIDE's `name_with_lsad`, so non-county jurisdictions read correctly ('Orleans Parish'). */
  name?: string;
  stateAbbreviation?: string;
  resolvedAt?: Date;
}

/** EC eligibility result values from Glide API */
export enum ECEligibilityResult {
  eligible = 'Eligible',
  ineligible = 'Ineligible',
}

export interface ITCAdderQualifications {
  lowIncomeCommunityEligible: boolean;
  incomeCategory?: string;
  energyCommunityEligible: boolean;
  ecEligibilityCriteria?: GlideECEligibility;
  ecCoalTractEligibilityCriteria?: GlideECCoalTractEligibility;
  ecFfeCountyMsaName?: string;
  ecFfeCountyMsaType?: string;
  ecFfeCountyMsaCode?: string;
  potentialEligibilityQueryDate?: Date;
  finalEligibilityQueryDate?: Date;
  domesticContentEligible?: boolean;
  /** Indicates EC eligibility check was performed at account creation */
  ecEligibilityCheckAccountCreation?: boolean;
  /** Indicates EC eligibility check was performed at PTO approval */
  ecEligibilityCheckPtoApproval?: boolean;
  /** EC eligibility result from Glide at account creation */
  ecEligibilityAccountCreation?: ECEligibilityResult;
  /** EC eligibility criteria (pricing tier) frozen at account creation */
  ecEligibilityCriteriaAccountCreation?: GlideECEligibility;
  /** EC eligibility result from Glide at PTO approval */
  ecEligibilityPtoApproval?: ECEligibilityResult;
  /** True when EC eligible at creation AND price mapping has EC Boost */
  ecBonusEligible?: boolean;
}

export enum PropertyOwnershipStatus {
  ineligiblePropertyType = 'ineligiblePropertyType',
  manualVerificationRequired = 'manualVerificationRequired',
  notFeeSimpleOwnership = 'notFeeSimpleOwnership',
  unverified = 'unverified',
  verified = 'verified',
}

export interface HVACPropertyDetails {
  homeSize?: number;
  existingSystems?: number;
  systemsBeingReplaced?: number;
}

export interface Utility {
  lseId: number;
  utilityName?: string;
  tariffId?: number;
  rate?: number;
  currentBalance?: number | null;
  currentBalanceValidDate?: IsoDateString | null;
}

export interface SystemComponent {
  manufacturer: string;
  model: string;
  count: number;
}

export interface SystemDetails {
  systemFirstYearProductionKwh: number;
  systemSizeKw: number;
  /** @deprecated  use inverters[]*/
  inverterManufacturer?: string;
  /** @deprecated  use inverters[]*/
  inverterModel?: string;
  /** @deprecated  use inverters[]*/
  inverterCount?: number;
  inverters?: SystemComponent[];
  panelManufacturer?: string;
  panelModel?: string;
  panelCount?: number;
  mountingManufacturer?: string;
  mountingModel?: string;
  mountingType?: string;
  mountingCount?: number;
  storage?: SystemComponent[];
  /** @deprecated use storage[] */
  storageManufacturer?: string;
  /** @deprecated use storage[] */
  storageModel?: string;
  /** @deprecated use storage[] */
  storageCount?: number;
  /** @deprecated use electricityUsageKwh */
  annualConsumption?: Array<{ month: number; consumption: number }>;
}

export enum AccountSearchField {
  address1 = 'address.address1',
  address2 = 'address.address2',
  addressCity = 'address.city',
  addressState = 'address.state',
  addressZIP = 'address.zip',
  applicantEmail = 'applicants.email',
  applicantFirstName = 'applicants.firstName',
  applicantLastName = 'applicants.lastName',
  externalReference = 'externalReference',
  friendlyName = 'friendlyName',
  organizationId = 'organizationId',
  primaryApplicantName = 'primaryApplicantName',
  salesRepName = 'salesRepName',
  status = 'status',
  externalReferenceId = 'externalReferenceIds.id',
}

export interface AccountNotes {
  milestones: {
    [MilestoneType.install]: string;
  };
}

export interface StrandedFulfillment {
  ownedByStrandedAssetTeam: boolean;
  history?: Array<{
    date: Date;
    action: 'assigned' | 'removed';
    userId: string;
  }>;
}

export enum ExternalReferenceType {
  concord = 'concord',
  doe = 'DOE', // Department of Energy
  sunpowerAccountId = 'sunpowerAccountId',
  sunpowerContractNumber = 'sunpowerContractNumber',
  sunpowerProjectId = 'sunpowerProjectId',
  sunpowerQuoteId = 'sunpowerQuoteId',
  importType = 'importType',
  trustEntity = 'trustEntity',
  ciaCustomer = 'ciaCustomer',
  ciaProperty = 'ciaProperty',
  contractOriginator = 'contractOriginator',
  dispatchProjectId = 'dispatchProjectId',
  dispatchId = 'dispatchId',
}

export interface ExternalReferenceId {
  id: string;
  type: ExternalReferenceType;
}

export type AccountExpandKey = 'maintenanceContract';

export interface IAccountService<T extends ProgramType> {
  programType: T;
  createAccount(organizationId: string, body: CreateAccountRequestV2<T>): Promise<AccountDocument<T>>;
  createAccountSideEffects(account: AccountDocument<T>): Promise<void>;
  getAccountById(accountId: string, expand?: AccountExpandKey[]): Promise<AccountDocument<T>>;
  updateAccount(id: string, data: any, bypassValidation?: boolean): Promise<AccountDocument<T>>;
}

export type BundleError = {
  programType: ProgramType;
  reason: string;
};

export type CreateAccountRequestV2<T extends ProgramType = any> = CreateAccountDetails<T> & {
  externalReference?: string;
  externalReferenceIds?: ExternalReferenceId[];
  programType: T;
  organizationId: string;
  friendlyName?: string;
  address: Address;
  applicants: Applicant[];
  coordinates?: Coordinates;
  language?: Locale;
  salesRepName: string;
  salesRepLicenseNumber?: string;
  salesRepEmail?: string;
  salesRepPhoneNumber?: string;
  salesRepIdProviderToken?: string;
  acquisitionChannel?: string;
  brand?: string;
  originatingPlatform?: string;
  systemDesign?: SystemDesignReturnType<T>;
};

export type HVACServicingContractor = {
  name: string;
  email?: string;
  phoneNumber?: string;
  address?: Address;
};

export type CreateHVACAccountDetails = {
  servicingContractor?: HVACServicingContractor;
  propertyDetails?: HVACPropertyDetails;
};

export type WaterHeaterServicingContractor = {
  name: string;
  email?: string;
  phoneNumber?: string;
  address?: Address;
};

export type CreateWaterHeaterAccountDetails = {
  servicingContractor?: WaterHeaterServicingContractor;
  propertyDetails?: WaterHeaterPropertyDetails;
};

export type CreateGeothermalAccountDetails = {
  propertyDetails?: GeothermalPropertyDetails;
};

export type GeneratorServicingContractor = {
  name: string;
  email?: string;
  phoneNumber?: string;
  address?: Address;
};

export type CreateGeneratorAccountDetails = {
  servicingContractor?: GeneratorServicingContractor;
  propertyDetails?: GeneratorPropertyDetails;
};

export type CreateSolarAccountDetails = {
  utility: Utility;
  permitSubmittedDate?: ProjectDates;
  permitApprovedDate?: ProjectDates;
  installScheduledDate?: ProjectDates;
  scheduledInspectionDate?: ProjectDates;
  expectedPTODate?: ProjectDates;
  electricityUsageKwh?: number;
  electricityUsageSource?: string;
};

export type CreateAccountDetails<T extends ProgramType> = T extends 'hvac'
  ? CreateHVACAccountDetails
  : T extends 'waterHeater'
    ? CreateWaterHeaterAccountDetails
    : T extends 'generator'
      ? CreateGeneratorAccountDetails
      : T extends 'geothermal'
        ? CreateGeothermalAccountDetails
        : T extends 'consumerSolar'
          ? CreateSolarAccountDetails
          : CreateSolarAccountDetails;

export type PtoGreenlight = {
  greenlightStatus: boolean;
  trancheClosed: boolean;
  tranchedClosedOn: Date;
  manualOverrideDate?: Date;
  manualOverrideUser?: string;
};

export enum AccountExportLookups {
  incomingInvoices = 'incomingInvoices',
  tasks = 'tasks',
}

export interface AccountSummaryBody {
  query: string;
  paging: {
    pageSize: number;
    pageNum: number;
  };
  milestoneQueryType: MilestoneType | 'undefined';
  filters: string[];
  includeCompletedAccounts: boolean;
  onlyCancelledAccounts: boolean;
  onlyPostActivationAccounts: boolean;
  organizations: string[];
  accountIds?: string[];
  agingBucketData?: Record<AgingQuery, AgingAccountData[]>;
  additionalLookups?: AccountExportLookups[];
  customProjection?: Record<string, number>;
}

export type AccountSummaryFields = AccountDocument & {
  daysAging?: Record<AgingQuery, number>;
  queues?: Partial<QueueDocument>[];
  applications?: Partial<ApplicationDocument>[];
  agingView?: Partial<AgingViewDocument>[];
  quotes?: Partial<QuoteDocument>[];
  hasPendingChangeOrder?: boolean;
};

export interface AccountSummaryResponse {
  total: number;
  pageNum: number;
  data: {
    accounts: AccountSummaryFields[];
    milestoneSummary: {
      totalCount: number;
      milestones: any[];
    };
  };
}

export enum SearchAccountsSort {
  MostRelevant = 'MOST_RELEVANT',
  Newest = 'NEWEST',
  Oldest = 'OLDEST',
  NewestUpdates = 'NEWEST_UPDATES',
  OldestUpdates = 'OLDEST_UPDATES',
  /**
   * Newest first, with `_id` as a deterministic tiebreaker. Used by paged exports so that
   * accounts sharing a `meta.createdAt` value keep a stable order across page boundaries
   * (preventing duplicated/skipped rows under `$skip`/`$limit` pagination).
   */
  NewestThenId = 'NEWEST_THEN_ID',
}

export const incentiveStatusLabels: Record<IncentiveAssignmentStatus, string> = {
  [IncentiveAssignmentStatus.active]: 'Active',
  [IncentiveAssignmentStatus.blocked]: 'Blocked',
  [IncentiveAssignmentStatus.complete]: 'Complete',
  [IncentiveAssignmentStatus.unallocated]: 'Unallocated',
};
